<script lang="ts" setup>
  import SnippetIcon from '@/components/icons/snippet.vue'
  import Popover from '@/components/ui/popover/Popover.vue'
  import PopoverContent from '@/components/ui/popover/PopoverContent.vue'
  import PopoverTrigger from '@/components/ui/popover/PopoverTrigger.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import type { CustomerCase } from '@/types/generated'
  import { SavedResponsesService } from '@/types/generated/services.gen'
  import { AIChatSessionTypeEnum } from '@/types/generated/types.gen'
  import { FileTextIcon, MoreHorizontalIcon, XIcon } from 'lucide-vue-next'
  import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import EditSavedResponseDialog from './EditSavedResponseDialog.vue'

  const { t } = useTranslation({
    en: {
      Response: 'Snippet',
      Responses: 'Snippets',
      NoSavedResponses: 'No saved snippets',
      SavedAIResponsesWillBeDisplayedHere: 'Saved AI responses will be displayed here',
      GenerateDocument: 'Generate document',
      EditResponse: 'Edit response',
      DeleteResponse: 'Delete response',
      UseResponse: 'Use response',
      ConfirmDelete: 'Confirm Delete',
      DeleteConfirmationMessage:
        'Are you sure you want to delete this saved response? This action cannot be undone.',
      Cancel: 'Cancel',
      Delete: 'Delete'
    },
    de: {
      Response: 'Snippet',
      Responses: 'Snippets',
      NoSavedResponses: 'Keine gespeicherten Snippets',
      SavedAIResponsesWillBeDisplayedHere: 'Gespeicherte KI-Antworten werden hier angezeigt',
      GenerateDocument: 'Dokument generieren',
      EditResponse: 'Antwort bearbeiten',
      DeleteResponse: 'Antwort löschen',
      UseResponse: 'Antwort verwenden',
      ConfirmDelete: 'Löschen bestätigen',
      DeleteConfirmationMessage:
        'Sind Sie sicher, dass Sie diese gespeicherte Antwort löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      Cancel: 'Abbrechen',
      Delete: 'Löschen'
    }
  })

  const props = defineProps<{
    caseData: CustomerCase | null
  }>()

  // Inject session type from parent AiChat component
  const sessionType = inject<keyof typeof AIChatSessionTypeEnum>(
    'sessionType',
    AIChatSessionTypeEnum.CASE
  )

  const isOpen = ref(false)
  const savedResponses = ref<any[]>([])
  const isLoading = ref(true)
  const openPopoverId = ref<string | null>(null)
  const selectedResponse = ref<any>(null)
  const showEditDialog = ref(false)
  const showDeleteConfirmation = ref(false)
  const responseToDelete = ref<any>(null)

  const responsesCount = computed(() => savedResponses.value.length)

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (!content || content.length <= maxLength) return content
    return content.substring(0, maxLength) + '...'
  }

  const togglePopover = (id: string) => {
    openPopoverId.value = id === openPopoverId.value ? null : id
  }

  const editResponse = (response: any) => {
    selectedResponse.value = response
    showEditDialog.value = true
    openPopoverId.value = null
    isOpen.value = false
  }

  const confirmDeleteResponse = (response: any) => {
    responseToDelete.value = response
    showDeleteConfirmation.value = true
    openPopoverId.value = null
  }

  const deleteResponse = async () => {
    if (!responseToDelete.value) return

    try {
      await SavedResponsesService.savedResponsesDestroy({ uuid: responseToDelete.value.uuid })
      await loadSavedResponses()
      showDeleteConfirmation.value = false
      responseToDelete.value = null
    } catch (error) {
      console.error('Error deleting saved response:', error)
    }
  }

  const cancelDelete = () => {
    showDeleteConfirmation.value = false
    responseToDelete.value = null
  }

  const useResponse = (response: any) => {
    // Emit event to insert response content into chat input
    window.dispatchEvent(
      new CustomEvent('use-saved-response', {
        detail: { content: response.content }
      })
    )
    isOpen.value = false
  }

  const loadSavedResponses = async () => {
    // For global context, we don't need a case UUID
    if (sessionType === AIChatSessionTypeEnum.CASE && !props.caseData?.uuid) return

    isLoading.value = true
    try {
      // Load saved responses filtered by session type
      const response = await SavedResponsesService.savedResponsesList({
        sessionType: sessionType
      })

      // Sort by creation date
      savedResponses.value = (response.results || []).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    } catch (error) {
      console.error('Error loading saved responses:', error)
      savedResponses.value = []
    } finally {
      isLoading.value = false
    }
  }

  // Load saved responses when component mounts, case changes, or session type changes
  watch(() => [props.caseData?.uuid, sessionType], loadSavedResponses, { immediate: true })

  // Listen for saved response events to refresh the list
  const handleResponseSaved = () => {
    loadSavedResponses()
  }

  onMounted(() => {
    window.addEventListener('save-response-dialog-closed', handleResponseSaved)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('save-response-dialog-closed', handleResponseSaved)
  })
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger
      class="flex items-center gap-1.5 text-xs hover:text-gray-700 dark:hover:text-neutral-300"
      data-cy="saved-snippet-popover-trigger"
    >
      <SnippetIcon class="h-3.5 w-3.5" />
      <span>
        {{ responsesCount === 1 ? `1 ${t('Response')}` : `${responsesCount} ${t('Responses')}` }}
      </span>
    </PopoverTrigger>

    <PopoverContent
      class="w-[400px] p-0"
      side="bottom"
      align="end"
      :side-offset="8"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 class="text-base font-medium">{{ t('Responses') }} ({{ responsesCount }})</h3>
        <button
          class="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-300"
          @click="isOpen = false"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div>

      <!-- Content -->
      <div class="max-h-[400px] overflow-y-auto">
        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-8"
        >
          <div class="text-sm text-gray-500">Loading...</div>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="savedResponses.length === 0"
          class="flex flex-col items-center justify-center py-8 text-center"
        >
          <div class="mb-4 rounded-full bg-gray-100 p-3 dark:bg-neutral-800">
            <FileTextIcon class="h-6 w-6 text-gray-400" />
          </div>
          <h4 class="mb-2 text-sm font-medium text-gray-900 dark:text-neutral-100">
            {{ t('NoSavedResponses') }}
          </h4>
          <p class="text-xs text-gray-500 dark:text-neutral-400">
            {{ t('SavedAIResponsesWillBeDisplayedHere') }}
          </p>
        </div>

        <!-- Responses list -->
        <div
          v-else
          class="space-y-2 p-4"
        >
          <div
            v-for="response in savedResponses"
            :key="response.uuid"
            class="group relative rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50
              dark:border-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700"
          >
            <div class="flex items-start justify-between">
              <div
                class="min-w-0 flex-1 cursor-pointer"
                @click="editResponse(response)"
              >
                <h4 class="text-sm font-medium text-gray-900 dark:text-neutral-100">
                  {{ response.title }}
                </h4>
                <p class="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                  {{ truncateContent(response.content) }}
                </p>
                <p class="mt-2 text-xs text-gray-400">
                  {{ formatDate(response.created_at) }}
                </p>
              </div>

              <!-- Options menu -->
              <div class="relative">
                <button
                  class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-neutral-600
                    dark:hover:text-neutral-300"
                  @click="togglePopover(response.uuid)"
                >
                  <MoreHorizontalIcon class="h-4 w-4" />
                </button>

                <!-- Dropdown menu -->
                <div
                  v-if="openPopoverId === response.uuid"
                  class="absolute right-0 top-8 z-10 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg
                    dark:border-neutral-700 dark:bg-neutral-800"
                >
                  <button
                    class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100
                      dark:text-neutral-300 dark:hover:bg-neutral-700"
                    @click="editResponse(response)"
                  >
                    {{ t('EditResponse') }}
                  </button>
                  <button
                    class="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-100
                      dark:hover:bg-neutral-700"
                    @click="confirmDeleteResponse(response)"
                  >
                    {{ t('DeleteResponse') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Generate document button -->
        <!-- <div
          v-if="savedResponses.length > 0"
          class="border-t border-gray-200 p-4 dark:border-neutral-700"
        >
          <button
            class="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2
              text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-neutral-600 dark:bg-neutral-800
              dark:text-neutral-300 dark:hover:bg-neutral-700"
          >
            <PlusIcon class="h-4 w-4" />
            {{ t('GenerateDocument') }}
          </button>
        </div> -->
      </div>
    </PopoverContent>
  </Popover>

  <!-- Edit Dialog -->
  <Teleport to="body">
    <EditSavedResponseDialog
      v-if="showEditDialog && selectedResponse"
      :snippet="selectedResponse"
      @close="showEditDialog = false"
      @updated="loadSavedResponses"
    />
  </Teleport>

  <!-- Delete Confirmation Dialog -->
  <Teleport to="body">
    <div
      v-if="showDeleteConfirmation"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click="cancelDelete"
    >
      <div
        class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800"
        @click.stop
      >
        <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-neutral-100">
          {{ t('ConfirmDelete') }}
        </h3>
        <p class="mb-6 text-sm text-gray-600 dark:text-neutral-300">
          {{ t('DeleteConfirmationMessage') }}
        </p>
        <div class="flex gap-3">
          <button
            class="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
              hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
              dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600"
            @click="cancelDelete"
          >
            {{ t('Cancel') }}
          </button>
          <button
            class="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            @click="deleteResponse"
          >
            {{ t('Delete') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
